import { useEffect, useRef, type RefObject } from 'react';

/** How the overlay was asked to close. Escape means cancel; a click outside does not. */
export type DismissSource = 'pointer' | 'escape';

/**
 * Close an open overlay on an outside pointer press or on Escape.
 *
 * Lifted out of SelectFilter when the range filters grew panels of their own:
 * three copies of this effect would have drifted the way the filter-row styles
 * already had. The source is passed on because the two gestures do not mean the
 * same thing — the range panel reverts an unapplied draft on Escape and keeps it
 * on a click outside. `onDismiss` is held in a ref so a caller passing an inline
 * arrow does not resubscribe the listeners on every render.
 */
export function useDismissable(
  ref: RefObject<HTMLElement | null>,
  isOpen: boolean,
  onDismiss: (source: DismissSource) => void
): void {
  const dismiss = useRef(onDismiss);
  dismiss.current = onDismiss;

  useEffect(() => {
    if (!isOpen) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) dismiss.current('pointer');
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') dismiss.current('escape');
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [ref, isOpen]);
}
